import type { NavigateFunction } from "react-router-dom";

export const CALENDAR_ORIGIN = "calendar";

export const calendarVisitState = { from: CALENDAR_ORIGIN } as const;

const isInternalPath = (value: string) =>
  value.startsWith("/") && !value.startsWith("//");

export const readReturnPath = (state: unknown): string | undefined => {
  if (!state || typeof state !== "object") {
    return undefined;
  }

  const from = (state as { from?: unknown }).from;
  if (typeof from !== "string" || !isInternalPath(from)) {
    return undefined;
  }

  return from;
};

export const isCalendarOrigin = (search: string, state: unknown) => {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  if (params.get("from") === CALENDAR_ORIGIN) {
    return true;
  }

  if (!state || typeof state !== "object") {
    return false;
  }

  return (state as { from?: unknown }).from === CALENDAR_ORIGIN;
};

export const withCalendarOrigin = (path: string) => {
  const [pathname, search = ""] = path.split("?");
  const params = new URLSearchParams(search);
  params.set("from", CALENDAR_ORIGIN);
  const query = params.toString();

  return {
    to: query ? `${pathname}?${query}` : pathname,
    state: calendarVisitState,
  };
};

export const calendarItemPath = (item: {
  id: string;
  type: "activity" | "event" | "ritual_bath";
  activityType?: "ACTIVITY" | "RITUAL";
}) => {
  if (item.type === "ritual_bath") {
    return `/ritual-baths/${item.id}`;
  }
  if (item.type === "event") {
    return `/events/${item.id}`;
  }
  return item.activityType === "RITUAL"
    ? `/rituals/${item.id}`
    : `/activities/${item.id}`;
};

export const resolveVisitBackTarget = ({
  search,
  state,
  listPath,
}: {
  search: string;
  state: unknown;
  listPath: string;
}) => readReturnPath(state) ?? (isCalendarOrigin(search, state) ? "/" : listPath);

export const navigateBackOrParent = (
  navigate: NavigateFunction,
  parent: string,
) => {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  if (typeof idx === "number" && idx > 0) {
    navigate(-1);
    return;
  }

  navigate(parent);
};

export const forgetLegacyCalendarOrigin = (
  kind: "event" | "activity" | "ritual_bath",
  id: string | undefined,
) => {
  if (!id) {
    return;
  }

  sessionStorage.removeItem(`fromCalendar:${kind}:${id}`);
};
